import { msg } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { Alert, Anchor, Button, Code, Collapse, Container, Group, Stack, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { Component, type ErrorInfo } from "react"
import { i18n } from "../../lib/i18n"
import type { IProps, IState } from "./interfaces"

/**
 * Collapsible raw-error disclosure. The boundary shows a generic recovery
 * sentence as the primary text; the underlying message (often a stack-derived
 * "Cannot read properties of undefined…") stays behind this "Details" toggle.
 * A functional subcomponent so it can use hooks — the boundary itself must
 * stay a class component for `componentDidCatch`.
 */
const ErrorDetails = ({ detail }: { detail: string }): React.JSX.Element => {
  const [open, { toggle }] = useDisclosure(false)
  return (
    <Stack gap={4}>
      <Anchor component="button" type="button" size="xs" c="dimmed" onClick={toggle}>
        {open ? <Trans>Hide details</Trans> : <Trans>Details</Trans>}
      </Anchor>
      <Collapse in={open}>
        <Code block>{detail}</Code>
      </Collapse>
    </Stack>
  )
}

/**
 * Render-error boundary.
 *
 * - Catches throws in its sub-tree (`getDerivedStateFromError`).
 * - Relays the error to diagnostics via the `onError` prop (the root wires
 *   this to the existing `/diagnostics/relay` channel); falls back to
 *   `console.error` when no relay is supplied.
 * - Recovers without a full reload: the built-in fallback offers "Try again"
 *   (clears error state) alongside "Go to home page". `resetKeys` clears the
 *   error automatically when surrounding context changes.
 * - Reusable for sub-trees: pass `children`, an optional render `fallback`,
 *   `onReset`, and `resetKeys`.
 *
 * Must stay a class component — `componentDidCatch` has no hook equivalent.
 */
export class ErrorBoundary extends Component<IProps, IState> {
  state: IState = { error: null }

  static getDerivedStateFromError(error: Error): IState {
    return { error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    } else {
      // No relay supplied (e.g. unauthenticated tree, or a boundary mounted
      // without the root's relay callback). Keep a breadcrumb in the console.
      // TODO: wire a default relay once a hook-free company-id accessor exists.
      console.error("[ErrorBoundary] uncaught render error", error, errorInfo.componentStack)
    }
  }

  componentDidUpdate(prevProps: IProps): void {
    if (this.state.error === null) return
    if (this.resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset()
    }
  }

  private resetKeysChanged(
    prev: ReadonlyArray<unknown> | undefined,
    next: ReadonlyArray<unknown> | undefined,
  ): boolean {
    if (prev === next) return false
    if (prev === undefined || next === undefined) return prev !== next
    if (prev.length !== next.length) return true
    return prev.some((value, index) => !Object.is(value, next[index]))
  }

  private reset = (): void => {
    this.setState({ error: null })
    this.props.onReset?.()
  }

  render(): React.ReactNode {
    const { error } = this.state

    if (error) {
      if (this.props.fallback) {
        return this.props.fallback({ error, reset: this.reset })
      }

      return (
        <Container size="sm" py="xl">
          <Stack align="center" gap="md">
            <Alert color="danger" title={i18n._(msg`Something went wrong`)} variant="light" w="100%">
              <Text size="sm" mb="sm">
                {i18n._(msg`An unexpected error interrupted this page. Try again, or head back to the home page.`)}
              </Text>
              <Group gap="xs" mb="sm">
                <Button size="xs" variant="filled" color="brand" onClick={this.reset}>
                  {i18n._(msg`Try again`)}
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    this.reset()
                    window.location.href = "/"
                  }}
                >
                  {i18n._(msg`Go to home page`)}
                </Button>
              </Group>
              {error.message ? <ErrorDetails detail={error.message} /> : null}
            </Alert>
          </Stack>
        </Container>
      )
    }

    return this.props.children
  }
}
